import React from "react";
import { useTranslation } from "react-i18next";
import { CreatePodcastRequestDtoCefrLevel } from "@packages/api/madlenAPI.schemas";

interface CEFRSelectorProps {
  selectedLevel: CreatePodcastRequestDtoCefrLevel;
  onLevelChange: (level: CreatePodcastRequestDtoCefrLevel) => void;
  className?: string;
  themeColor?: "purple" | "blue" | "green" | "orange" | "red" | "teal";
}

const CEFRSelector: React.FC<CEFRSelectorProps> = ({
  selectedLevel,
  onLevelChange,
  className = "",
  themeColor = "blue",
}) => {
  const { t } = useTranslation();

  const levels = [
    {
      value: CreatePodcastRequestDtoCefrLevel.PRE_A1,
      code: "Pre-A1",
      name: t("cefr.preA1", "Pre-Basic"),
    },
    {
      value: CreatePodcastRequestDtoCefrLevel.A1,
      code: "A1",
      name: t("cefr.a1", "Beginner"),
    },
    {
      value: CreatePodcastRequestDtoCefrLevel.A2,
      code: "A2",
      name: t("cefr.a2", "Elementary"),
    },
    {
      value: CreatePodcastRequestDtoCefrLevel.B1,
      code: "B1",
      name: t("cefr.b1", "Intermediate"),
    },
    {
      value: CreatePodcastRequestDtoCefrLevel.B2,
      code: "B2",
      name: t("cefr.b2", "Upper-Int."),
    },
    {
      value: CreatePodcastRequestDtoCefrLevel.C1,
      code: "C1",
      name: t("cefr.c1", "Advanced"),
    },
    {
      value: CreatePodcastRequestDtoCefrLevel.C2,
      code: "C2",
      name: t("cefr.c2", "Proficiency"),
    },
  ];

  const getThemeClasses = () => {
    const themes = {
      purple: {
        selected: "bg-purple-600 text-white shadow-md dark:bg-purple-500",
        unselected:
          "text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-white/50 dark:hover:bg-gray-700/50",
        selectedSubtext: "text-purple-100 dark:text-purple-200",
      },
      blue: {
        selected: "bg-blue-600 text-white shadow-md dark:bg-blue-500",
        unselected:
          "text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-white/50 dark:hover:bg-gray-700/50",
        selectedSubtext: "text-blue-100 dark:text-blue-200",
      },
      green: {
        selected: "bg-green-600 text-white shadow-md dark:bg-green-500",
        unselected:
          "text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-white/50 dark:hover:bg-gray-700/50",
        selectedSubtext: "text-green-100 dark:text-green-200",
      },
      teal: {
        selected: "bg-teal-600 text-white shadow-md dark:bg-teal-500",
        unselected:
          "text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-white/50 dark:hover:bg-gray-700/50",
        selectedSubtext: "text-teal-100 dark:text-teal-200",
      },
      orange: {
        selected: "bg-orange-600 text-white shadow-md dark:bg-orange-500",
        unselected:
          "text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-white/50 dark:hover:bg-gray-700/50",
        selectedSubtext: "text-orange-100 dark:text-orange-200",
      },
      red: {
        selected: "bg-red-600 text-white shadow-md dark:bg-red-500",
        unselected:
          "text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-white/50 dark:hover:bg-gray-700/50",
        selectedSubtext: "text-red-100 dark:text-red-200",
      },
    };
    return themes[themeColor];
  };

  const themeClasses = getThemeClasses();

  return (
    <div className={`w-full ${className}`}>
      <div className="flex items-center space-x-1 bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
        {levels.map((level) => (
          <button
            key={level.value}
            type="button"
            onClick={() => onLevelChange(level.value)}
            className={`
              flex-1 px-2 py-2.5 rounded-lg transition-all duration-200 min-w-0
              ${
                selectedLevel === level.value
                  ? themeClasses.selected
                  : themeClasses.unselected
              }
            `}
            data-testid={`cefr-level-${level.code.toLowerCase()}`}
          >
            <div className="text-center">
              <div className="font-bold text-xs sm:text-sm truncate">
                {level.code}
              </div>
              <div
                className={`text-xs mt-0.5 truncate ${
                  selectedLevel === level.value
                    ? themeClasses.selectedSubtext
                    : "text-gray-500 dark:text-gray-400"
                }`}
              >
                {level.name}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default CEFRSelector;
